import { notify } from '@/core/toast/notify';
import { ApiError, getApiErrorMessage } from '@/utils/apiError';

export function resolveSsoErrorMessage(
  code: string | null,
  detail: string | null,
  flow: 'login' | 'register',
): string {
  if (!code) return '';

  const messages: Record<string, string> =
    flow === 'register'
      ? {
          access_denied: 'Google sign-up was cancelled.',
          account_not_found:
            'This Google account is already registered. Sign in instead, or use a different email.',
          email_exists: 'This Google email is already registered. Sign in instead.',
          invalid_state: 'Google sign-up expired. Please try again.',
          missing_code: 'Google sign-up did not complete. Please try again.',
          oauth_error: 'Google sign-up is not configured correctly. Use the form below or contact support.',
          profile_incomplete: 'Google did not share your email. Allow email access and try again.',
          sso_failed: 'Google sign-up failed. Use the form below or sign in if you already have an account.',
        }
      : {
          access_denied: 'Google sign-in was cancelled.',
          account_not_found: 'This Google account is not registered.',
          email_exists: 'This Google email is already registered. Sign in with Google or your password.',
          invalid_state: 'Google sign-in expired. Please try again.',
          missing_code: 'Google sign-in did not complete. Please try again.',
          oauth_error: 'Google sign-in is not configured correctly. Use email and password or contact support.',
          profile_incomplete: 'Google did not share your email. Allow email access and try again.',
          sso_failed: 'Google sign-in failed. Use email and password, or create an account first.',
        };

  const base =
    messages[code] ??
    (flow === 'register'
      ? 'Google sign-up could not be completed.'
      : 'Google sign-in could not be completed.');

  if (detail && (code === 'oauth_error' || code === 'sso_failed' || code === 'profile_incomplete')) {
    const lower = detail.toLowerCase();
    if (lower.includes('not registered') || lower.includes('no account found')) {
      return flow === 'login' ? 'This Google account is not registered.' : 'This Google account is already registered.';
    }
    return `${base} (${detail})`;
  }
  return base;
}

/** Maps SSO token exchange API failures to short user-facing text. */
export function resolveSsoExchangeError(err: unknown, flow: 'login' | 'register' = 'login'): string {
  const api = ApiError.fromUnknown(err);
  const message = api.message.toLowerCase();

  if (api.code === 'INVALID_TOKEN' || message.includes('expired') || message.includes('invalid or expired')) {
    return 'Google sign-in expired. Please try again.';
  }
  if (
    api.code === 'FORBIDDEN' ||
    api.code === 'RESOURCE_NOT_FOUND' ||
    message.includes('not registered') ||
    message.includes('no account found')
  ) {
    return flow === 'login' ? 'This Google account is not registered.' : 'This Google account is already registered.';
  }
  if (message.includes('not enabled')) {
    return 'Google sign-in is not enabled. Use email and password instead.';
  }
  return getApiErrorMessage(err, resolveSsoErrorMessage('sso_failed', null, flow));
}

export function ssoErrorToastGuardKey(scope: string, code: string, detail?: string | null): string {
  return `sso-error-toast:${scope}:${code}:${detail ?? ''}`;
}

/** Show SSO error toast; dedupe rapid repeats (e.g. React StrictMode) without blocking later retries. */
export function showSsoErrorOnce(message: string, guardKey: string): boolean {
  if (!message.trim()) return false;
  return notifySsoError(message, guardKey);
}

const recentSsoToasts = new Map<string, number>();
const SSO_TOAST_DEDUPE_MS = 2500;

function notifySsoError(message: string, guardKey: string): boolean {
  const now = Date.now();
  const last = recentSsoToasts.get(guardKey);
  if (last != null && now - last < SSO_TOAST_DEDUPE_MS) {
    return false;
  }
  recentSsoToasts.set(guardKey, now);
  notify.error(message);
  return true;
}

export { notifySsoError };

export function ssoExchangeGuardKey(exchangeToken: string): string {
  return `oauth:sso-exchange:${exchangeToken}`;
}

export function ssoExchangeMessageKey(exchangeToken: string): string {
  return `${ssoExchangeGuardKey(exchangeToken)}:message`;
}
